import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ACAO_AUDITADA, type AcaoAuditada } from '@arena/shared/dominio';
import {
  RECURSOS_AUDITADOS,
  RECURSOS_DE_TITULAR,
  ROTULO_RECURSO_AUDITADO,
} from '@arena/shared/schemas';
import { auditoriaApi, baixarArquivo, perfisApi, type FiltrosDoConsole } from '../api/acessos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { Erro, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Botao, Campo, Formulario, Tabela, Vazio } from '../ui/primitivos.jsx';
import { classeDaAcao, quandoLocal, resumoDoDetalhe } from './formatos.js';

/**
 * O console de auditoria completo — a tela cheia que o protótipo NÃO
 * desenhou (o feed de `#v-acessos` é o resumo dela). Segue o padrão de
 * tabela com filtros do documento 05.
 *
 * O filtro por TITULAR é a pergunta que a LGPD exige responder em uma
 * consulta: "quem acessou os dados desta pessoa, e quando?" — por isso
 * ele tem os dois campos próprios (recurso + identificador) em vez de
 * um campo de texto livre que a pessoa teria que formatar à mão.
 *
 * Paginação por cursor via `useInfiniteQuery`: mudar um filtro troca a
 * `queryKey` e reseta as páginas automaticamente — não precisa de
 * efeito manual acumulando linha.
 *
 * Ver: Arena_backend/Docs/plano/19-permissoes-auditoria.md
 */

interface FormularioDeFiltro {
  autor: string;
  acao: AcaoAuditada | '';
  recurso: string;
  registro: string;
  titularRecurso: string;
  titularId: string;
  de: string;
  ate: string;
}

const FORM_VAZIO: FormularioDeFiltro = {
  autor: '',
  acao: '',
  recurso: '',
  registro: '',
  titularRecurso: '',
  titularId: '',
  de: '',
  ate: '',
};

function paraFiltros(form: FormularioDeFiltro): FiltrosDoConsole {
  return {
    ...(form.autor ? { autor: form.autor } : {}),
    ...(form.acao ? { acao: form.acao } : {}),
    ...(form.recurso ? { recurso: form.recurso } : {}),
    ...(form.registro.trim() ? { registro: form.registro.trim() } : {}),
    ...(form.titularRecurso && form.titularId.trim()
      ? { titular: `${form.titularRecurso}:${form.titularId.trim()}` }
      : {}),
    ...(form.de ? { de: form.de } : {}),
    ...(form.ate ? { ate: form.ate } : {}),
  };
}

export function PaginaDoConsole() {
  const [form, setForm] = useState<FormularioDeFiltro>(FORM_VAZIO);
  const [filtros, setFiltros] = useState<FiltrosDoConsole>({});
  const [erroExportacao, setErroExportacao] = useState<string | null>(null);

  const equipe = useQuery({ queryKey: ['acessos', 'equipe'], queryFn: perfisApi.equipe });

  const consulta = useInfiniteQuery({
    queryKey: ['auditoria', 'console', filtros],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      auditoriaApi.pagina(filtros, pageParam, 25),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  async function exportar() {
    setErroExportacao(null);
    try {
      const { nomeDoArquivo, conteudo } = await auditoriaApi.exportar(filtros);
      baixarArquivo(nomeDoArquivo, conteudo);
    } catch (e) {
      setErroExportacao(mensagemDoErro(e));
    }
  }

  const itens = consulta.data?.pages.flatMap((p) => p.itens) ?? [];
  const fuso = consulta.data?.pages[consulta.data.pages.length - 1]?.fuso ?? 'America/Sao_Paulo';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Console de auditoria</h1>
          <p>Quem leu, quem alterou, quem anonimizou — e quando. A trilha só é lida aqui.</p>
        </div>
        <div className="head-actions">
          <Botao variante="contorno" onClick={() => void exportar()}>
            Exportar CSV
          </Botao>
        </div>
      </div>

      {erroExportacao && (
        <div className="empty" role="alert" style={{ borderColor: 'var(--red)' }}>
          {erroExportacao}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2>Filtros</h2>
        </div>
        <Formulario
          colunas={3}
          onSubmit={(e) => {
            e.preventDefault();
            setFiltros(paraFiltros(form));
          }}
        >
          <Campo rotulo="Autor">
            <select
              value={form.autor}
              onChange={(e) => setForm({ ...form, autor: e.target.value })}
            >
              <option value="">Qualquer um</option>
              {equipe.data?.map((m) => (
                <option key={m.usuarioId} value={m.usuarioId}>
                  {m.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Ação">
            <select
              value={form.acao}
              onChange={(e) => setForm({ ...form, acao: e.target.value as AcaoAuditada | '' })}
            >
              <option value="">Qualquer uma</option>
              {ACAO_AUDITADA.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Recurso">
            <select
              value={form.recurso}
              onChange={(e) => setForm({ ...form, recurso: e.target.value })}
            >
              <option value="">Qualquer um</option>
              {RECURSOS_AUDITADOS.map((r) => (
                <option key={r} value={r}>
                  {(ROTULO_RECURSO_AUDITADO as Record<string, string>)[r]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Registro (id)">
            <input
              value={form.registro}
              onChange={(e) => setForm({ ...form, registro: e.target.value })}
              placeholder="id do registro"
            />
          </Campo>
          <Campo rotulo="Titular — recurso">
            <select
              value={form.titularRecurso}
              onChange={(e) => setForm({ ...form, titularRecurso: e.target.value })}
            >
              <option value="">Nenhum</option>
              {RECURSOS_DE_TITULAR.map((r) => (
                <option key={r} value={r}>
                  {(ROTULO_RECURSO_AUDITADO as Record<string, string>)[r]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Titular — identificador" ajuda="quem acessou os dados desta pessoa">
            <input
              value={form.titularId}
              onChange={(e) => setForm({ ...form, titularId: e.target.value })}
              placeholder="id da pessoa"
              disabled={!form.titularRecurso}
            />
          </Campo>
          <Campo rotulo="De">
            <input
              type="date"
              value={form.de}
              onChange={(e) => setForm({ ...form, de: e.target.value })}
            />
          </Campo>
          <Campo rotulo="Até">
            <input
              type="date"
              value={form.ate}
              onChange={(e) => setForm({ ...form, ate: e.target.value })}
            />
          </Campo>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <Botao type="submit">Filtrar</Botao>
            <Botao
              variante="fantasma"
              type="button"
              onClick={() => {
                setForm(FORM_VAZIO);
                setFiltros({});
              }}
            >
              Limpar
            </Botao>
          </div>
        </Formulario>
      </div>

      {consulta.isPending && <EsqueletoDeTabela linhas={8} colunas={6} />}

      {consulta.isError && (
        <Erro
          detalhe={mensagemDoErro(consulta.error)}
          onTentarDeNovo={() => void consulta.refetch()}
        />
      )}

      {consulta.data && itens.length === 0 && <Vazio>Nenhum evento com estes filtros.</Vazio>}

      {consulta.data && itens.length > 0 && (
        <>
          <Tabela colunas={['Quando', 'Quem', 'Ação', 'Recurso', 'Registro', 'Detalhe']}>
            {itens.map((evento) => (
              <tr key={evento.id}>
                <td>{quandoLocal(evento.ocorridoEm, fuso)}</td>
                <td>{evento.autor?.nome ?? 'sem sessão'}</td>
                <td>
                  <span className={`status ${classeDaAcao(evento.acao)}`}>{evento.acao}</span>
                </td>
                <td>
                  {(ROTULO_RECURSO_AUDITADO as Record<string, string>)[evento.recurso] ??
                    evento.recurso}
                </td>
                <td>{evento.pessoa?.nome ?? evento.registroId ?? '—'}</td>
                <td className="sub">{resumoDoDetalhe(evento.detalhe)}</td>
              </tr>
            ))}
          </Tabela>

          {consulta.hasNextPage && (
            <div style={{ marginTop: 14, textAlign: 'center' }}>
              <Botao
                variante="contorno"
                onClick={() => void consulta.fetchNextPage()}
                disabled={consulta.isFetchingNextPage}
              >
                {consulta.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Botao>
            </div>
          )}
        </>
      )}
    </>
  );
}
